import { useEffect, useRef, useState } from 'react';
import { useTabs } from '../../context/TabsContext';

export function stackFromTab(tab) {
  if (tab?.type !== 'doc') return [];
  if (Array.isArray(tab.docStack) && tab.docStack.length) return tab.docStack;
  if (tab.targetId) return [tab.targetId];
  return [];
}

export function stacksEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((id, i) => id === b[i]);
}

/**
 * Keep the local link trail in sync with the active doc tab, and write it
 * back so leaving the tab (or unmounting Docs) doesn't forget it.
 *
 * Empty local stack = sheet closed. That is not written to the tab, so
 * clicking the tab again can restore the saved trail.
 */
export function useDocTabMemory(activeTab, docStack, setDocStack) {
  const { updateTab } = useTabs();
  const updateTabRef = useRef(updateTab);
  updateTabRef.current = updateTab;

  const [stackTabId, setStackTabId] = useState(
    activeTab?.type === 'doc' ? activeTab.id : null
  );

  // Adopt the new tab's trail before children commit, so two doc tabs
  // never share a frame of each other's stack.
  if (activeTab?.type === 'doc' && activeTab.id !== stackTabId) {
    setStackTabId(activeTab.id);
    const next = stackFromTab(activeTab);
    if (!stacksEqual(docStack, next)) setDocStack(next);
  }

  if (activeTab?.type !== 'doc' && stackTabId != null) {
    setStackTabId(null);
    if (docStack.length) setDocStack([]);
  }

  useEffect(() => {
    if (activeTab?.type !== 'doc' || !docStack.length) return undefined;
    const tabId = activeTab.id;
    const stack = docStack;
    const tip = stack[stack.length - 1];
    updateTabRef.current(tabId, { docStack: stack, targetId: tip });
    return () => {
      updateTabRef.current(tabId, { docStack: stack, targetId: tip });
    };
  }, [activeTab?.id, activeTab?.type, docStack]);
}
